import { type LoaderFunctionArgs } from 'react-router';

import {
  type UsStateConfig,
  type UsTerritoryConfig,
} from '@dnc/baseline/contentful/types';
import type { Jurisdiction, State } from '@dnc/baseline/data/jurisdictions';
import { Jurisdictions } from '@dnc/baseline/data/jurisdictions';
import type { Option } from '@dnc/baseline/utils/option';

import { Services } from '../services/services';
import { TypedUrlHelper } from '../services/url-helper';

import { enforceJurisdictionLocale } from './enforceJurisdictionLocale';
import { fetchJurisdictionFromSearchQueryOrGeo } from './fetchJurisdictionFromSearchQueryOrGeo';
import { fetchUsTerritoryConfig } from './fetchTerritoryConfig';
import { fetchUsStateConfig } from './fetchUsStateConfig';

/**
 * Type for when we may or may not have a jurisdiction, and we may or may not
 * have loaded its config. The loaded config will be {@link UsStateConfig}
 * for US States, and {@link UsTerritoryConfig} for US Territories, when loaded.
 */
export type OptionalLoadedJurisdictionInfo =
  | {
      // Note: If you change this name, update `useLoadedJurisdiction` in
      // GaAnalyticsProvider.tsx.
      jurisdiction: undefined;
      jurisdictionConfig: undefined;
    }
  | {
      // Note: If you change this name, update `useLoadedJurisdiction` in
      // GaAnalyticsProvider.tsx.
      jurisdiction: Jurisdiction;
      jurisdictionConfig: Option<UsStateConfig | UsTerritoryConfig>;
    };

/**
 * A type narrowed version of {@link OptionalLoadedJurisdictionInfo}, where
 * we can be confident that:
 * - `TypedOptionalLoadedJurisdictionInfo<State>["jurisdictionConfig"]` will be {@link UsStateConfig}, and
 * - `TypedOptionalLoadedJurisdictionInfo<Territory>["jurisdictionConfig"]` will be {@link UsTerritoryConfig}
 */
export type TypedOptionalLoadedJurisdictionInfo<T extends Jurisdiction> = {
  jurisdiction: OptionalLoadedJurisdictionInfo['jurisdiction'];
  jurisdictionConfig: T extends State
    ? Option<UsStateConfig>
    : Option<UsTerritoryConfig>;
};

/**
 * Determines the jurisdiction and loads the jurisdictionConfig from the "state"
 * query parameter. If it’s not provided, uses {@link JurisdictionService} to
 * look it up via geo-ip.
 *
 * If the value of the `state` parameter is not a valid jurisdiction, redirects
 * to the same URL, but without a `state` parameter.
 *
 * Respects a secret `no_geo` query parameter that prevents it from loading
 * geo-ip data, for easier testing of those cases.
 *
 * This may return a `jurisdiction` but not a `jurisdictionConfig` if there was
 * an error loading from Contentful, in which case routes may use the VEP widget
 * fallback.
 */
export async function loadJurisdictionFromSearchQuery(
  services: Pick<Services, 'voterEducation' | 'jurisdiction' | 'sentry'>,
  { request }: LoaderFunctionArgs
): Promise<OptionalLoadedJurisdictionInfo> {
  const urlHelper = TypedUrlHelper.fromRequestUrl(request.url, {
    enforce: true,
  });

  const jurisdiction = await fetchJurisdictionFromSearchQueryOrGeo(
    services,
    urlHelper
  );

  if (!jurisdiction) {
    return {
      jurisdiction: undefined,
      jurisdictionConfig: undefined,
    };
  } else {
    let jurisdictionConfig: Option<UsStateConfig | UsTerritoryConfig> =
      undefined;

    try {
      if (Jurisdictions.isTerritory(jurisdiction)) {
        jurisdictionConfig = await fetchUsTerritoryConfig(
          services,
          urlHelper,
          jurisdiction
        );
      } else if (Jurisdictions.isState(jurisdiction)) {
        jurisdictionConfig = await fetchUsStateConfig(
          services,
          urlHelper,
          jurisdiction
        );
      }
    } catch (error: any) {
      console.error(`Failure loading jurisdiction: ${jurisdiction}`, error);

      // Log the error in Sentry
      services.sentry.record(error);
    }

    if (jurisdictionConfig) {
      enforceJurisdictionLocale(
        request.url,
        urlHelper.getLocale(),
        jurisdictionConfig
      );
    }

    return {
      jurisdiction,
      jurisdictionConfig,
    };
  }
}
